import { Plus, X, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface ChoiceOptionsProps {
  options: string[]
  onChange: (options: string[]) => void
  error?: string
}

export function ChoiceOptions({
  options,
  onChange,
  error,
}: ChoiceOptionsProps) {
  const { t } = useTranslation()

  const addOption = () => {
    onChange([...options, ''])
  }

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options]
    newOptions[index] = value
    onChange(newOptions)
  }

  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index)
    onChange(newOptions)
  }

  const moveOptionUp = (index: number) => {
    if (index <= 0) return

    const newOptions = [...options]
    const temp = newOptions[index]
    newOptions[index] = newOptions[index - 1]
    newOptions[index - 1] = temp
    onChange(newOptions)
  }

  const moveOptionDown = (index: number) => {
    if (index >= options.length - 1) return

    const newOptions = [...options]
    const temp = newOptions[index]
    newOptions[index] = newOptions[index + 1]
    newOptions[index + 1] = temp
    onChange(newOptions)
  }

  return (
    <div className='space-y-3'>
      <Label>{t('surveys.options')}</Label>
      {error && <p className='text-sm text-red-600'>{error}</p>}
      <div className='space-y-2'>
        {options.map((option, index) => (
          <div key={index} className='flex items-center gap-2'>
            <Input
              value={option}
              onChange={(e) => updateOption(index, e.target.value)}
              placeholder={`${t('surveys.option')} ${index + 1}`}
              className='flex-1'
            />
            <div className='flex items-center gap-1'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => moveOptionUp(index)}
                disabled={index === 0}
                title={t('surveys.moveUp')}
              >
                <ChevronUp className='h-4 w-4' />
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => moveOptionDown(index)}
                disabled={index === options.length - 1}
                title={t('surveys.moveDown')}
              >
                <ChevronDown className='h-4 w-4' />
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => removeOption(index)}
                disabled={options.length <= 2}
                title={t('surveys.removeOption')}
                className='text-destructive hover:text-destructive'
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={addOption}
          className='w-full'
        >
          <Plus className='mr-2 h-4 w-4' />
          {t('surveys.addOption')}
        </Button>
      </div>
      {options.length < 2 && (
        <p className='text-sm text-muted-foreground'>
          {t('surveys.atLeastTwoOptionsRequired')}
        </p>
      )}
    </div>
  )
}
